import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mail, 
  Send, 
  Settings, 
  CheckCircle, 
  XCircle, 
  Plus, 
  X, 
  RefreshCw,
  Clock,
  AlertTriangle,
  FileText,
  Car,
  User,
  History
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailConfigTab = ({ getAuthHeaders }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('config');
  
  // Dialog state for adding emails
  const [addEmailDialog, setAddEmailDialog] = useState({ open: false, field: null });
  const [newEmailInput, setNewEmailInput] = useState('');

  // Default config structure
  const defaultConfig = {
    enabled: true,
    from_email: "notificari@resend.dev",
    from_name: "C.Manager Notificări",
    cm_enabled: true,
    cm_to_emails: [],
    cm_cc_emails: [],
    cm_subject_template: "CM Nou - {employee_name} - {date}",
    car_docs_enabled: false,
    car_docs_to_emails: [],
    car_docs_cc_emails: [],
    car_docs_days_before: 30,
    car_docs_subject_template: "Expirare Document - {car_name} - {doc_type}",
    driver_docs_enabled: false,
    driver_docs_to_emails: [],
    driver_docs_cc_emails: [],
    driver_docs_days_before: 30
  };

  // Fetch email configuration
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/email/config`, getAuthHeaders());
      // Merge with defaults to ensure all fields exist
      setConfig({ ...defaultConfig, ...response.data });
    } catch (error) {
      console.error('Failed to fetch email config:', error);
      toast.error('Eroare la încărcarea configurației email');
      // Set default config on error
      setConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch email logs
  const fetchLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/email/logs?limit=50`, getAuthHeaders());
      // Asigurăm că emailLogs este întotdeauna un array
      const logs = Array.isArray(response.data) ? response.data : 
                   (response.data?.logs || response.data?.data || []);
      setEmailLogs(logs);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
      setEmailLogs([]); // Reset to empty array on error
    }
  }, [getAuthHeaders]);

  // Fetch email stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/email/stats`, getAuthHeaders());
      setEmailStats(response.data);
    } catch (error) {
      console.error('Failed to fetch email stats:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchConfig();
    fetchLogs();
    fetchStats();
  }, [fetchConfig, fetchLogs, fetchStats]);

  // Save configuration
  const saveConfig = async () => {
    try {
      setSaving(true);
      console.log('Saving config:', config);
      const response = await axios.post(`${API}/api/email/config`, config, getAuthHeaders());
      console.log('Save response:', response.data);
      toast.success('Configurația a fost salvată cu succes!');
      // Re-fetch to confirm
      fetchConfig();
    } catch (error) {
      console.error('Save error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Eroare la salvarea configurației';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Send test email
  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Introduceți o adresă de email');
      return;
    }
    
    try {
      setSendingTest(true);
      await axios.post(`${API}/api/email/test`, {
        to_email: testEmail,
        subject: 'Test Email - C.Manager',
        message: 'Acesta este un email de test trimis din aplicația C.Manager pentru a verifica configurarea serviciului de email.'
      }, getAuthHeaders());
      toast.success(`Email de test trimis către ${testEmail}`);
      setTestEmail('');
      fetchLogs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la trimiterea email-ului de test');
    } finally {
      setSendingTest(false);
    }
  };

  // Add email to list
  const addEmail = (field) => {
    setAddEmailDialog({ open: true, field });
    setNewEmailInput('');
  };
  
  const confirmAddEmail = () => {
    const email = newEmailInput.trim();
    if (email && email.includes('@') && email.includes('.')) {
      setConfig(prev => ({
        ...prev,
        [addEmailDialog.field]: [...(prev[addEmailDialog.field] || []), email]
      }));
      setAddEmailDialog({ open: false, field: null });
      setNewEmailInput('');
      toast.success(`Email ${email} adăugat. Nu uitați să salvați configurația!`);
    } else {
      toast.error('Introduceți o adresă de email validă');
    }
  };

  // Remove email from list
  const removeEmail = (field, index) => {
    setConfig(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Mail className="w-7 h-7 text-blue-600" />
          Configurare Notificări Email
        </h1>
        <p className="text-slate-600 mt-1">
          Configurați trimiterea automată de email-uri pentru diferite evenimente din aplicație
        </p>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Configurare
          </TabsTrigger>
          <TabsTrigger value="test" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Test Email
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Istoric ({emailLogs.length})
          </TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-6">
          {/* Statistics Cards */}
          {emailStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Email-uri trimise</p>
                      <p className="text-2xl font-bold text-slate-900">{emailStats.total_sent}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Erori</p>
                      <p className="text-2xl font-bold text-slate-900">{emailStats.total_errors}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Notificări CM</p>
                      <p className="text-2xl font-bold text-slate-900">{emailStats.by_type?.cm_notification || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Global Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Setări Generale
              </CardTitle>
              <CardDescription>
                Configurați setările globale pentru serviciul de email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Serviciu Email Activ</Label>
                  <p className="text-sm text-slate-500">Activează/dezactivează trimiterea de email-uri</p>
                </div>
                <Switch
                  checked={config?.enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nume Expeditor</Label>
                  <Input
                    value={config?.from_name || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, from_name: e.target.value }))}
                    placeholder="C.Manager Notificări"
                  />
                </div>
                <div>
                  <Label>Email Expeditor</Label>
                  <Input
                    value={config?.from_email || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, from_email: e.target.value }))}
                    placeholder="notificari@resend.dev"
                    disabled
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Email-ul expeditor este configurat de Resend
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CM Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Notificări Concediu Medical (CM)
              </CardTitle>
              <CardDescription>
                Trimite email automat când se adaugă un CM cu document atașat
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Notificări CM Active</Label>
                  <p className="text-sm text-slate-500">Trimite email când se adaugă CM cu document</p>
                </div>
                <Switch
                  checked={config?.cm_enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, cm_enabled: checked }))}
                />
              </div>

              <div>
                <Label>Subiect Email</Label>
                <Input
                  value={config?.cm_subject_template || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, cm_subject_template: e.target.value }))}
                  placeholder="CM Nou - {employee_name} - {date}"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Variabile disponibile: {'{employee_name}'}, {'{date}'}
                </p>
              </div>

              <div>
                <Label className="flex items-center justify-between">
                  <span>Destinatari (TO)</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => addEmail('cm_to_emails')}
                    className="h-7 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Adaugă
                  </Button>
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {config?.cm_to_emails?.length === 0 && (
                    <p className="text-sm text-slate-400 italic">Niciun destinatar configurat</p>
                  )}
                  {config?.cm_to_emails?.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {email}
                      <button
                        onClick={() => removeEmail('cm_to_emails', index)}
                        className="hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <Label className="flex items-center justify-between">
                  <span>Copie (CC)</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => addEmail('cm_cc_emails')}
                    className="h-7 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Adaugă
                  </Button>
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {config?.cm_cc_emails?.length === 0 && (
                    <p className="text-sm text-slate-400 italic">Niciun CC configurat</p>
                  )}
                  {config?.cm_cc_emails?.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-sm"
                    >
                      {email}
                      <button
                        onClick={() => removeEmail('cm_cc_emails', index)}
                        className="hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Car Documents Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5 text-purple-500" />
                Notificări Documente Mașini (În curând)
              </CardTitle>
              <CardDescription>
                Alertă pentru documente mașini care expiră
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Notificări Expirare Documente</Label>
                  <p className="text-sm text-slate-500">Trimite alertă înainte de expirarea documentelor</p>
                </div>
                <Switch
                  checked={config?.car_docs_enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, car_docs_enabled: checked }))}
                />
              </div>

              {config?.car_docs_enabled && (
                <>
                  <div>
                    <Label>Zile înainte de expirare</Label>
                    <Input
                      type="number"
                      value={config?.car_docs_days_before || 30}
                      onChange={(e) => setConfig(prev => ({ ...prev, car_docs_days_before: parseInt(e.target.value) || 30 }))}
                      min={1}
                      max={90}
                    />
                  </div>

                  <div>
                    <Label className="flex items-center justify-between">
                      <span>Destinatari (TO)</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addEmail('car_docs_to_emails')}
                        className="h-7 text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Adaugă
                      </Button>
                    </Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {config?.car_docs_to_emails?.length === 0 && (
                        <p className="text-sm text-slate-400 italic">Niciun destinatar configurat</p>
                      )}
                      {config?.car_docs_to_emails?.map((email, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                        >
                          {email}
                          <button
                            onClick={() => removeEmail('car_docs_to_emails', index)}
                            className="hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={saveConfig}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Se salvează...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Salvează Configurația
                </>
              )}
            </Button>
          </div>
        </TabsContent>

        {/* Test Email Tab */}
        <TabsContent value="test">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-green-500" />
                Trimite Email de Test
              </CardTitle>
              <CardDescription>
                Verifică dacă serviciul de email funcționează corect
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Adresa de email pentru test</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="exemplu@email.com"
                    className="flex-1"
                  />
                  <Button
                    onClick={sendTestEmail}
                    disabled={sendingTest || !testEmail}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {sendingTest ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Se trimite...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Trimite Test
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">Informații</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Email-ul de test va fi trimis folosind serviciul Resend</li>
                  <li>• Verificați și folderul de spam dacă nu primiți email-ul</li>
                  <li>• Primele email-uri pot dura câteva minute să ajungă</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-500" />
                  Istoric Email-uri
                </CardTitle>
                <CardDescription>
                  Ultimele {emailLogs.length} email-uri trimise
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { fetchLogs(); fetchStats(); }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reîncarcă
              </Button>
            </CardHeader>
            <CardContent>
              {(!emailLogs || !Array.isArray(emailLogs) || emailLogs.length === 0) ? (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nu există email-uri în istoric</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailLogs.map((log, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        log.status === 'sent' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {log.status === 'sent' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className="font-medium text-slate-900">
                            {log.type === 'test' ? 'Test' : 
                             log.type === 'cm_notification' ? 'Notificare CM' : log.type}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(log.sent_at).toLocaleString('ro-RO')}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        <p><strong>Către:</strong> {Array.isArray(log.to) ? log.to.join(', ') : log.to}</p>
                        {log.subject && <p><strong>Subiect:</strong> {log.subject}</p>}
                        {log.employee_name && <p><strong>Angajat:</strong> {log.employee_name}</p>}
                        {log.error && <p className="text-red-600"><strong>Eroare:</strong> {log.error}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Dialog pentru adăugare email */}
      <Dialog open={addEmailDialog.open} onOpenChange={(open) => setAddEmailDialog({ open, field: addEmailDialog.field })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adaugă adresă email</DialogTitle>
            <DialogDescription>
              Introduceți adresa de email pe care doriți să o adăugați la listă.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Adresa de email</Label>
              <Input
                type="email"
                value={newEmailInput}
                onChange={(e) => setNewEmailInput(e.target.value)}
                placeholder="exemplu@email.com"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmAddEmail();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEmailDialog({ open: false, field: null })}>
              Anulează
            </Button>
            <Button onClick={confirmAddEmail} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Adaugă
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailConfigTab;
